import { Control, useWatch } from 'react-hook-form';
import {
  buildCoverageComparisonSet,
  draftBlockId,
  DraftCoverageBlock,
} from './coverageHoursComparisonSet';
import { createCoverageHoursConflictViewModel } from './coverageHoursConflictViewModel';
import { hasCoverageConflict } from './coverageHoursOverlap';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';

type OverlapFormValues = {
  teamId: string | null;
  blocks: DraftCoverageBlock[];
};

export type UseCoverageHoursOverlapArgs = {
  control: Control<OverlapFormValues>;
  bankedEntries: TeamCoverageEntry[];
  editingEntryId?: string;
  channelId: string;
  category: string;
  issue: string;
};

export type UseCoverageHoursOverlapResult = {
  hasConflictAt: (index: number) => boolean;
  validateNoOverlap: (
    blocks: DraftCoverageBlock[],
    values: Pick<OverlapFormValues, 'teamId'>
  ) => boolean;
};

export function useCoverageHoursOverlap({
  control,
  bankedEntries,
  editingEntryId,
  channelId,
  category,
  issue,
}: UseCoverageHoursOverlapArgs): UseCoverageHoursOverlapResult {
  const watchedBlocks = useWatch({ control, name: 'blocks' });
  const watchedTeamId = useWatch({ control, name: 'teamId' });

  const comparisonSetFor = (
    draftBlocks: DraftCoverageBlock[],
    draftTeamId: string | null
  ) =>
    buildCoverageComparisonSet({
      draftBlocks,
      draftTeamId,
      bankedEntries,
      editingEntryId,
      channelId,
      category,
      issue,
    });

  const watchedComparisonSet = comparisonSetFor(watchedBlocks, watchedTeamId);

  const hasConflictAt = (index: number) =>
    createCoverageHoursConflictViewModel({
      blocks: watchedComparisonSet,
      blockId: draftBlockId(index),
    }).hasConflict;

  // The rule reads the values it is handed rather than the watched ones: validation
  // runs ahead of the re-render that would refresh them.
  const validateNoOverlap = (
    blocks: DraftCoverageBlock[],
    values: Pick<OverlapFormValues, 'teamId'>
  ) => !hasCoverageConflict(comparisonSetFor(blocks, values.teamId));

  return { hasConflictAt, validateNoOverlap };
}
